import { useId, type ReactNode } from 'react';
import { Box, Divider, Stack, Text } from '@citibox-panel/ui';
import type { DeliveryDetail } from '@citibox-panel/api-client';
import { authorizedDisplayName } from '../lib/authorizedDisplayName';
import { titularDisplayName } from '../lib/titularDisplayName';

export interface DeliverySharingCardProps {
  recipient: DeliveryDetail['recipient'];
  authorized: DeliveryDetail['sharedWith'];
  // «Cambiar titular», handed down already wired. An OPAQUE node on purpose, in the idiom of the
  // timeline's `extendCustodyControl`: this section decides WHERE the control goes, never what it
  // does nor WHETHER it is offered. Eligibility is a property of the «Entrega»'s status, which
  // lives with the layout — and the layout already answers that same question for the six actions
  // in the card above.
  changeRecipientAction?: ReactNode;
}

// The read-only «Titular y autorizados» block of the «Entrega» detail: who the parcel is for, and
// who else was authorized to pick it up. It renders ALWAYS.
//
// It used to render only when somebody was authorized (CBX-10547), on the grounds that the absence
// was how «not shared» got said and an empty state would make the majority case look broken. That
// rested on the section being EMPTY without authorized pickups, and it is not: it holds the
// titular, which the panel showed nowhere else — so on an unshared «Entrega», the majority case,
// the agent could not read who the parcel was for, let alone check a reassignment. What survives
// of the old argument is the HEADING: «Titular y autorizados» would lie where there are none, so
// it names only what it actually holds.
//
// It deliberately does NOT wrap itself in a `Card`/`CardContent`. It is handed to the «Acciones»
// card as an opaque node and mounted behind a full-bleed rule INSIDE it — one card with two
// sections, not two cards. Wrapping it here would put a card inside a card and break that rule's
// bleed, so if this looks like it is missing its container: it is not, the host owns it.
//
// Phones are painted VERBATIM — E.164, no grouping, never truncated — because the panel has no
// phone formatter and the same person must carry the same string on every screen. They are also
// the one thing here an agent reads aloud or copies, which is why the de-emphasized tone is
// `muted` (grey.600, 4.61:1) and not `secondary` (#ACA5BB, 2.37:1): de-emphasis must not cost
// legibility on a value that gets transcribed.
// TODO: i18n — copy en ES hasta Fase 1.
const HEADING_WITH_AUTHORIZED = 'Titular y autorizados';
const HEADING_TITULAR_ONLY = 'Titular';
const SHARED_WITH = 'COMPARTIDO CON';

export function DeliverySharingCard({
  recipient,
  authorized,
  changeRecipientAction,
}: DeliverySharingCardProps) {
  // The label is rendered once and pointed at, never restated in an aria-label: one string on
  // screen and in the accessible name means they cannot drift.
  const labelId = useId();
  // What the section holds is now the only thing conditional here, and it does NOT depend on who is
  // asking: the same delivery reads the same from the titular's ficha and from a location's tab.
  const hasAuthorized = authorized.length > 0;
  const titularPhone = recipient?.phone ?? null;
  return (
    <Stack spacing={2}>
      <Text variant="body1" component="h3" bold>
        {hasAuthorized ? HEADING_WITH_AUTHORIZED : HEADING_TITULAR_ONLY}
      </Text>
      {/* A row, so the action sits at the far edge across from the titular. Top-aligned rather
          than centred: it belongs to the person named on the first line, and centring it against a
          two-line block would leave it floating between the name and the phone. */}
      <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Text variant="body2" bold>{titularDisplayName(recipient)}</Text>
          {/* Rendered only when there IS one, and it grows no fallback of its own: an em dash under
              an em dash would state the same absence twice, and an empty <Text> would take up the
              line without saying anything at all. */}
          {titularPhone !== null && (
            <Text variant="body2" color="muted">
              {titularPhone}
            </Text>
          )}
        </Box>
        {changeRecipientAction}
      </Stack>
      {hasAuthorized && (
        <>
        {/* 8px above and below. The two gaps are set in DIFFERENT units of measure, so read the note
            before touching them.
            Two things worth knowing before touching these numbers. `&&` is load-bearing: Stack lays
            its spacing on `& > :not(style) + :not(style)`, which outspecifies a plain margin on the
            child, so without doubling the class the margin-TOP silently loses and only the bottom
            half applies. And the two gaps come from different elements — above is this rule's own
            margin-top, below is the NEXT block's, which is why it is tuned with a margin-BOTTOM.
            So `mt` is the gap above OUTRIGHT (it replaces Stack's 16px), while `mb` only ADJUSTS the
            gap below (16px + mb). Same number in both means two different distances. */}
        <Divider textAlign="left" sx={{ '&&': { mt: 1, mb: -1 } }}>
          <Text variant="overline" color="secondary" bold dense id={labelId}>
            {SHARED_WITH}
          </Text>
        </Divider>
        <Box role="group" aria-labelledby={labelId}>
          <Stack spacing={2}>
            {/* Keyed by POSITION first, because the identity of a row is the SHARE and not the person:
                backend does not deduplicate, so the same account — or the same account-less phone —
                can legitimately appear twice, and there is no share id on the wire to tell the two
                apart. Position is safe to lean on here in a way it usually is not: the list is
                server-ordered, never reordered client-side, and its rows hold no state to lose. The
                person is appended only so the key still reads as something in devtools. */}
            {authorized.map((person, position) => (
              <Box
                key={`${String(position)}:${person.id === null ? 'no-account' : String(person.id)}`}
              >
                <Text variant="body2" bold>{authorizedDisplayName(person)}</Text>
                <Text variant="body2" color="muted">
                  {person.phone}
                </Text>
              </Box>
            ))}
          </Stack>
        </Box>
        </>
      )}
    </Stack>
  );
}
